import React from 'react';
import PropTypes from 'prop-types';
import {Card, Divider, Grid, Hidden, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {colors} from '../../../assets/styles/colors';
import {useHistory} from 'react-router-dom';
import {ORDER_STATE_COLORS} from '../../../entities';
import type {User} from '../../../entities';

type Label = 'PENDING' | 'PAYED' | 'DELIVERED';

type Props = {|
  quantity: number,
  totalAmount: string,
  label: Label,
  color: string,
  currencySymbol: string,
  user?: User,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    minHeight: '100%',
    background: '#fffff',
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.up('lg')]: {
      minHeight: 193,
      justifyContent: 'center',
      flexDirection: 'column',
    },
    '&:hover': {
      cursor: 'pointer',
      filter: 'brightness(0.95)',
    },
  },
  dividerQuantity: {
    backgroundColor: props => props.color,
    height: '4px',
    width: '28px',
    borderRadius: '5px',
    margin: 'auto',
  },
  totalAmount: {
    fontWeight: 500,
    color: colors.grey800,
    fontSize: '16px',
    textAlign: 'center',
  },
  center: {
    width: 'fit-content',
    margin: 'auto',
  },
  dividerBottom: {
    marginBottom: theme.spacing(2),
    backgroundColor: colors.grey300,
  },
  title: {
    textAlign: 'center',
    fontWeight: 500,
    color: colors.grey900,
    fontSize: '24px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    fontWeight: 500,
    color: colors.grey800,
    fontSize: '16px',
    textAlign: 'center',
    margin: theme.spacing(2, 0),
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    backgroundColor: colors.grey300,
  },
  wrapTotalAmount: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

function getLabel(label: string) {
  switch (label) {
    case 'PENDING':
      return 'Pedidos Pendientes';
    case 'CONFIRMED':
      return 'Pedidos Aceptados';
    case 'PAID':
      return 'Pedidos Pagados';
    case 'ON_THE_WAY':
      return 'Pedidos En ruta';
    case 'DELIVERED':
      return 'Pedidos Entregados';
    default:
      return 'Pedidos';
  }
}

function getId(state: string) {
  switch (state) {
    case 'PENDING':
      return 'pending-orders';
    case 'CONFIRMED':
      return 'accepted-orders';
    case 'PAID':
      return 'paid-orders';
    case 'ON_THE_WAY':
      return 'on-the-way-orders';
    case 'DELIVERED':
      return 'delivered-orders';
    default:
      return undefined;
  }
}

export default function OrderCard(props: Props) {
  const {label, quantity, totalAmount, currencySymbol, user} = props;
  const classes = useStyles({color: ORDER_STATE_COLORS[label].main});
  const history = useHistory();

  return (
    <Card
      className={classes.root}
      onClick={() => history.push(`/orders?states=${label}`)}
    >
      <Grid container style={{height: '100%'}}>
        <Grid item className={classes.center} xs={2} lg={12}>
          <Typography className={classes.title} id={getId(label)}>
            {quantity}
          </Typography>
          <Divider className={classes.dividerQuantity} />
        </Grid>
        <Grid item className={classes.center} xs={5} lg={12}>
          <Typography className={classes.label}>{getLabel(label)}</Typography>
        </Grid>
        {!!user && user.type === 'vendor-user' && (
          <Grid item xs={5} lg={12} className={classes.wrapTotalAmount}>
            <Hidden mdDown>
              <Divider className={classes.dividerBottom} />
            </Hidden>
            <Hidden lgUp>
              <Divider
                flexItem
                orientation="vertical"
                className={classes.dividerVertical}
              />
            </Hidden>
            <div className={classes.center}>
              <Typography className={classes.totalAmount}>
                {`${currencySymbol} ${totalAmount}`}
              </Typography>
            </div>
          </Grid>
        )}
      </Grid>
    </Card>
  );
}
OrderCard.propTypes = {
  quantity: PropTypes.number,
  totalAmount: PropTypes.string,
  label: PropTypes.string,
  user: PropTypes.object,
};
